/**
 * The human-decision record form (GO / CONDITIONAL / NO-GO / DEFER + rationale
 * + decision owner), rendered by the Decision tab's Decide section
 * (`DecideSurface`). The Path to GO Decide phase that also rendered it was
 * deleted in stage 3.
 *
 * Moved from `pathToGo/DecisionStage.tsx` (Path to GO removal, stage 1). The
 * gating is unchanged and deliberately PRESENTATIONAL: GO / CONDITIONAL are
 * disabled here when `positiveDecisionGateSatisfied` is false, the host
 * handler re-checks `pathToGoPositiveDecisionAllowed` before persisting, and
 * the server re-derives the gate from its own evidence. This component never
 * persists anything itself — it only calls `onRecordDecision`.
 */
import React from 'react';
import { LockKeyhole } from 'lucide-react';
import { Button } from '../../ui/button';
import type { PathToGoFinalDecision } from '../../../types/pathToGoDecision';

export interface PathToGoDecisionDraft {
  decision: PathToGoFinalDecision;
  rationale: string;
  decisionOwner: string;
}

export interface DecisionRecordFormProps {
  /** Callback bridge; the host builds the frozen evidence envelope + persists. */
  onRecordDecision: (draft: PathToGoDecisionDraft) => void | Promise<void>;
  /** `pathToGoPositiveDecisionAllowed(state)` — gates GO and CONDITIONAL. */
  positiveDecisionGateSatisfied: boolean;
  /** Pre-selected option on first render (GO when the verdict is GO, else DEFER). */
  initialDecision: PathToGoFinalDecision;
  decisionOwnerDefault?: string;
  /** Read-only viewer (site locked by someone else): every control disabled. */
  decisionReadOnly?: boolean;
}

/**
 * Shown when a positive decision is submitted while its gate is closed. Names
 * the production tab labels; with VITE_DECISION_FINDINGS_V1 rolled back the
 * Analyses tab is labelled "Reports" (accepted — production ships it on).
 */
export const POSITIVE_DECISION_GATE_MESSAGE =
  'Before recording GO or CONDITIONAL, make the decision intent current on Intent, the required analyses current on Analyses, and the verification work current on Site Evidence.';

export const DecisionRecordForm: React.FC<DecisionRecordFormProps> = ({
  onRecordDecision,
  positiveDecisionGateSatisfied,
  initialDecision,
  decisionOwnerDefault = '',
  decisionReadOnly = false,
}) => {
  const [decision, setDecision] = React.useState<PathToGoFinalDecision>(initialDecision);
  const [rationale, setRationale] = React.useState('');
  const [decisionOwner, setDecisionOwner] = React.useState(decisionOwnerDefault);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const submitDecision = async (event: React.FormEvent) => {
    event.preventDefault();
    if (decisionReadOnly || submitting) return;
    if ((decision === 'GO' || decision === 'CONDITIONAL') && !positiveDecisionGateSatisfied) {
      setSubmitError(POSITIVE_DECISION_GATE_MESSAGE);
      return;
    }
    if (!rationale.trim() || !decisionOwner.trim()) {
      setSubmitError('Add a rationale and decision owner.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onRecordDecision({
        decision,
        rationale: rationale.trim(),
        decisionOwner: decisionOwner.trim(),
      });
      setRationale('');
    } catch (error) {
      setSubmitError((error as Error)?.message ?? 'The decision could not be recorded.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      className="space-y-3 border-t border-border-subtle pt-4"
      onSubmit={submitDecision}
      data-testid="decision-record-form"
    >
      <div>
        <h3 className="type-body font-semibold text-text-primary">
          Record the final decision
        </h3>
        <p className="mt-1 max-w-prose text-sm text-text-muted" id="decision-record-guidance">
          Choose an outcome and explain your judgment. The record retains the intent, evidence, recommendation and Test Fit basis reviewed here.
        </p>
      </div>

      <fieldset className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <legend className="sr-only">Decision</legend>
        {(['GO', 'CONDITIONAL', 'NO_GO', 'DEFER'] as const).map((option) => {
          const positive = option === 'GO' || option === 'CONDITIONAL';
          const disabled = decisionReadOnly || (positive && !positiveDecisionGateSatisfied);
          return (
            <label
              key={option}
              className={`relative flex min-h-11 items-center justify-center gap-1.5 rounded-md border px-2 text-sm font-semibold focus-within:ring-2 focus-within:ring-primary ${
                decision === option
                  ? 'border-primary bg-primary/5 text-primary'
                  : disabled
                    ? 'cursor-not-allowed border-border-subtle text-text-muted opacity-50'
                    : 'cursor-pointer border-border text-text-secondary hover:text-text-primary'
              }`}
              data-testid={`decision-choice-${option}`}
              data-disabled={disabled ? 'true' : 'false'}
            >
              <input
                type="radio"
                name="path-decision"
                value={option}
                checked={decision === option}
                onChange={() => setDecision(option)}
                disabled={disabled}
                aria-describedby="decision-record-guidance"
                className="sr-only"
              />
              {disabled && <LockKeyhole className="h-3 w-3" aria-hidden="true" />}
              {option === 'NO_GO' ? 'NO-GO' : option}
            </label>
          );
        })}
      </fieldset>

      {!positiveDecisionGateSatisfied && (
        <p className="max-w-prose text-sm text-text-muted" data-testid="decision-positive-gate">
          GO and CONDITIONAL require current intent, analyses and verification. {decisionReadOnly ? 'This site is read-only.' : 'You can record NO-GO or DEFER now.'}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(12rem,0.45fr)]">
        <label className="block">
          <span className="type-meta font-bold text-text-secondary">Rationale</span>
          <textarea
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
            rows={3}
            disabled={decisionReadOnly}
            className="mt-1 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
            data-testid="decision-rationale"
          />
        </label>
        <label className="block">
          <span className="type-meta font-bold text-text-secondary">Decision owner</span>
          <input
            value={decisionOwner}
            onChange={(event) => setDecisionOwner(event.target.value)}
            disabled={decisionReadOnly}
            className="mt-1 min-h-11 w-full rounded-md border border-border bg-background px-3 text-sm text-text-primary outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50"
            data-testid="decision-owner"
          />
        </label>
      </div>

      {submitError && (
        <div role="alert" className="text-xs text-danger" data-testid="decision-record-error">
          {submitError}
        </div>
      )}

      <Button
        type="submit"
        variant="mission-primary"
        className="min-h-11"
        disabled={decisionReadOnly || submitting}
        data-testid="decision-record-submit"
      >
        {submitting ? 'Recording…' : 'Record decision'}
      </Button>
    </form>
  );
};

export default DecisionRecordForm;
